import Papa from 'papaparse';
import { calculateBusinessDaysBetween } from './dateUtils';
import type { Database } from '@/types/database.types';
import type { Sprint } from '@/types/sprint';

type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];
type Milestone = Database['public']['Tables']['milestones']['Row'];

export interface TaskDependency {
  task_id: number;
  depends_on_task_id: number;
}

interface ExportRow {
  project_name: string;
  task_id: number;
  task_name: string;
  task_type: string;
  status: string;
  assigned_member: string;
  start_date: string;
  end_date: string;
  duration_days: number;
  depends_on_tasks: string;
}

export interface ExportData {
  export_date: string;
  projects: Project[];
  tasks: Task[];
  team_members: TeamMember[];
  task_dependencies: TaskDependency[];
  milestones?: Milestone[];
  sprints?: Sprint[];
}

function formatDateForExport(dateStr: string): string {
  return dateStr.split('T')[0] ?? dateStr; // YYYY-MM-DD
}

export function exportToCSV(
  projects: Project[],
  tasks: Task[],
  teamMembers: TeamMember[],
  dependencies: TaskDependency[]
): string {
  const rows: ExportRow[] = tasks.map(task => {
    const project = projects.find(p => p.id === task.project_id);
    const member = teamMembers.find(m => m.id === task.assigned_member_id);
    const taskDeps = dependencies
      .filter(dep => dep.task_id === task.id)
      .map(dep => dep.depends_on_task_id)
      .join(';');

    const start = new Date(task.start_date + 'T00:00:00');
    const end = new Date(task.end_date + 'T00:00:00');
    const duration = calculateBusinessDaysBetween(start, end);

    return {
      project_name: project?.name ?? 'Unknown',
      task_id: task.id,
      task_name: task.name,
      task_type: task.type,
      status: task.status,
      assigned_member: member?.name ?? 'Unassigned',
      start_date: formatDateForExport(task.start_date),
      end_date: formatDateForExport(task.end_date),
      duration_days: duration,
      depends_on_tasks: taskDeps,
    };
  });

  return Papa.unparse(rows, {
    header: true,
    columns: [
      'project_name',
      'task_id',
      'task_name',
      'task_type',
      'status',
      'assigned_member',
      'start_date',
      'end_date',
      'duration_days',
      'depends_on_tasks',
    ],
  });
}

export function exportToJSON(
  projects: Project[],
  tasks: Task[],
  teamMembers: TeamMember[],
  dependencies: TaskDependency[],
  milestones?: Milestone[],
  sprints?: Sprint[]
): string {
  const exportData: ExportData = {
    export_date: new Date().toISOString(),
    projects,
    tasks,
    team_members: teamMembers,
    task_dependencies: dependencies,
    milestones,
    sprints,
  };
  return JSON.stringify(exportData, null, 2);
}

export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function generateExportFilename(prefix: string, format: 'csv' | 'json'): string {
  const iso = new Date().toISOString(); // e.g. 2026-05-13T14:30:45.000Z
  const date = iso.split('T')[0]!;     // YYYY-MM-DD (UTC)
  const time = iso.split('T')[1]!.slice(0, 8).replace(/:/g, ''); // HHmmss (UTC)
  return `${prefix}-${date}-${time}.${format}`;
}
